import type {
    FlexibleWorkingHoursInput,
    WorkLocationType,
    EnhancedLocation,
    ResolverContext,
    UpdateFlexibleWorkingHoursRequest,
    LocationSourceType,
    LocationIdType,
    LocationEntityType,
    InputMaybe,
    Exact,
} from 'owa-graph-schema';
import { getUserEmailAddress } from 'accelerator-aad/lib/getUserEmailAddress';
import {
    UpdateFlexibleWorkingHoursDocument,
    type UpdateFlexibleWorkingHoursMutation,
} from 'owa-calendar-working-hours-option/lib/graphql/__generated__/updateFlexibleWorkingHoursMutation.interface';
import getWorkLocationDetails from './getWorkLocationDetails';
import type { MailboxInfo } from 'owa-client-types';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';

export default function updateOccurringFlexibleWorkingHours(
    mailboxInfo: MailboxInfo,
    workHoursDef: FlexibleWorkingHoursInput,
    workLocationType?: WorkLocationType
): {
    mutation: TypedDocumentNode<
        UpdateFlexibleWorkingHoursMutation,
        Exact<{
            input: UpdateFlexibleWorkingHoursRequest;
        }>
    >;
    variables: {
        input: {
            mailboxId: string;
            updateDetails: {
                segment: {
                    id: string;
                    start: {
                        dateTime: string;
                        timeZone: {
                            name: string;
                        };
                    };
                    end: {
                        dateTime: string;
                        timeZone: {
                            name: string;
                        };
                    };
                    occurrenceDetails:
                        | {
                              seriesId: InputMaybe<string> | undefined;
                              seriesMasterId: InputMaybe<string> | undefined;
                          }
                        | undefined;
                    workLocationType: WorkLocationType;
                    workLocationDetails: {
                        displayName: string;
                        annotation: string;
                        postalAddress: {
                            street: string;
                            city: string;
                            state: string;
                            country: string;
                            postalCode: string;
                            postOfficeBox: string;
                        };
                        id: string;
                        idType: LocationIdType;
                        locationUri: string;
                        locationType: LocationEntityType;
                        locationSource: LocationSourceType;
                        coordinates: {
                            longitude: number;
                            latitude: number;
                            accuracy: number;
                            altitude: number;
                            altitudeAccuracy: number;
                        };
                    } | null;
                    subject: InputMaybe<string> | undefined;
                    isAllDay: InputMaybe<boolean> | undefined;
                };
            };
        };
    };
    context: ResolverContext | undefined;
} {
    const newWorkLocationType = workLocationType ?? workHoursDef.workLocationType;
    const isOffice: boolean = newWorkLocationType === 'Office';

    const workLocationDetails = getWorkLocationDetails(isOffice, {
        Id: workHoursDef?.workLocationDetails?.id,
        DisplayName: workHoursDef?.workLocationDetails?.displayName,
    } as EnhancedLocation);

    // occurrenceDetails property needs to be undefined for OOF segment creation scenario
    const occurrenceDetails = workHoursDef.occurrenceDetails
        ? {
              seriesId: workHoursDef.occurrenceDetails.seriesId,
              seriesMasterId: workHoursDef.occurrenceDetails.seriesMasterId,
          }
        : undefined;

    // This check is to ensure that the account selector can be rendered, which is
    // determined by the global settings account rather than being on a per-mailbox basis.
    const isMultiAccountEnabled = isGlobalFeatureEnabled('cal-surface-places-multi-account');

    const context = isMultiAccountEnabled
        ? ({
              requestOptions: {
                  mailboxInfo,
              },
          } as ResolverContext)
        : undefined;

    return {
        mutation: UpdateFlexibleWorkingHoursDocument,
        variables: {
            input: {
                mailboxId: isMultiAccountEnabled
                    ? mailboxInfo.mailboxSmtpAddress
                    : getUserEmailAddress(),
                updateDetails: {
                    segment: {
                        id: workHoursDef.id,
                        start: {
                            dateTime: workHoursDef.start.dateTime,
                            timeZone: {
                                name: workHoursDef.start.timeZone?.name ?? '',
                            },
                        },
                        end: {
                            dateTime: workHoursDef.end.dateTime,
                            timeZone: {
                                name: workHoursDef.end.timeZone?.name ?? '',
                            },
                        },
                        occurrenceDetails,
                        workLocationType: newWorkLocationType,
                        workLocationDetails,
                        subject: workHoursDef.subject,
                        isAllDay: workHoursDef.isAllDay,
                    },
                },
            },
        },
        context,
    };
}
